import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ls } from 'utils/my-localstorage'
import type { RetailWinesStore, Sorting, Filters, PageInfo } from './types'
import type { Currency } from 'api/schemas'
import { ManagementRoles } from 'utils/consts/userRoles'
import { RetailWinesCurrencyLocalStorage } from 'components/Pages/Retail/Wines/Content/_methods'

const getInitialCurrency = () => {
  const role = ls.getItem('wwl-role_code')
  const isAdmin = [
    ManagementRoles.SUPERADMIN,
    ManagementRoles.CONTENT,
    ManagementRoles.MANAGER,
  ].includes(role)

  const currencyFromLS = ls.getObject(RetailWinesCurrencyLocalStorage)

  return isAdmin
    ? currencyFromLS && currencyFromLS.id !== null
      ? currencyFromLS
      : null
    : null
}
export const LS_WINES_KEY = 'retail-wines-selectors'
export const LS_MY_RANGE_KEY = 'retail-my-range-selectors'

const readSelectorsFromLs = (lsKey: string) => {
  const saved = ls.getObject(lsKey) as
    | {
        wineLotBottleQuantity?: number[] | null
        deliveryCostCode?: string[] | null
        vatBehaviorCode?: string[] | null
        purchaseConditionCode?: Array<string | null> | null
      }
    | undefined
  if (!saved)
    return {
      wineLotBottleQuantity: undefined,
      deliveryCostCode: undefined,
      vatBehaviorCode: undefined,
      purchaseConditionCode: undefined,
      selectorsInitialized: false,
    }
  return {
    wineLotBottleQuantity: saved.wineLotBottleQuantity,
    deliveryCostCode: saved.deliveryCostCode,
    vatBehaviorCode: saved.vatBehaviorCode,
    purchaseConditionCode: saved.purchaseConditionCode,
    selectorsInitialized:
      saved.wineLotBottleQuantity !== undefined &&
      saved.deliveryCostCode !== undefined &&
      saved.vatBehaviorCode !== undefined &&
      saved.purchaseConditionCode !== undefined,
  }
}

const getInitialState: () => RetailWinesStore = () => ({
  items: [],
  pageInfo: null,
  sorting: {
    type: null,
    name: null,
    value: null,
  },
  loading: true,
  error: null,
  query: '',
  filters: [],
  selectedItems: [],
  currency: getInitialCurrency(),
  refetchTrigger: 0,
  selectorsLsKey: LS_WINES_KEY,
  ...readSelectorsFromLs(LS_WINES_KEY),
})
export interface CurrencyRetailWines extends Omit<Currency, 'id'> {
  id: number | null
}
const retailWinesSlice = createSlice({
  name: 'retailWines',
  initialState: getInitialState,
  reducers: {
    setItems: (state, { payload }: PayloadAction<any[]>) => {
      state.items = payload
    },
    setPageInfo: (state, { payload }: PayloadAction<PageInfo>) => {
      state.pageInfo = payload
    },
    setSortingValue: (state, { payload }: PayloadAction<Sorting | null>) => {
      state.sorting = payload
    },
    clear: () => {
      return getInitialState()
    },
    setLoading: (state, { payload }) => {
      state.loading = payload
    },
    setError: (state, { payload }) => {
      state.error = payload
    },
    setQuery: (state, { payload }: PayloadAction<string>) => {
      state.query = payload
      state.pageInfo = {
        ...state.pageInfo,
        page: 1,
      }
    },
    setFilters: (state, { payload }: PayloadAction<Filters>) => {
      state.filters = payload
      state.pageInfo = {
        ...state.pageInfo,
        page: 1,
      }
    },
    setSelectedItems: (state, { payload }) => {
      state.selectedItems = payload
    },
    setCurrency: (
      state,
      { payload }: PayloadAction<CurrencyRetailWines | null>
    ) => {
      state.currency = payload
    },
    setWineLotBottleQuantity: (
      state,
      { payload }: PayloadAction<number[] | null>
    ) => {
      state.wineLotBottleQuantity = payload
      state.selectorsInitialized =
        state.deliveryCostCode !== undefined &&
        state.vatBehaviorCode !== undefined &&
        state.purchaseConditionCode !== undefined
      ls.setObject(state.selectorsLsKey, {
        wineLotBottleQuantity: payload,
        deliveryCostCode: state.deliveryCostCode ?? null,
        vatBehaviorCode: state.vatBehaviorCode ?? null,
        purchaseConditionCode: state.purchaseConditionCode ?? null,
      })
    },
    setDeliveryCostCode: (
      state,
      { payload }: PayloadAction<string[] | null>
    ) => {
      state.deliveryCostCode = payload
      state.selectorsInitialized =
        state.wineLotBottleQuantity !== undefined &&
        state.vatBehaviorCode !== undefined &&
        state.purchaseConditionCode !== undefined
      ls.setObject(state.selectorsLsKey, {
        wineLotBottleQuantity: state.wineLotBottleQuantity ?? null,
        deliveryCostCode: payload,
        vatBehaviorCode: state.vatBehaviorCode ?? null,
        purchaseConditionCode: state.purchaseConditionCode ?? null,
      })
    },
    setVatBehaviorCode: (
      state,
      { payload }: PayloadAction<string[] | null>
    ) => {
      state.vatBehaviorCode = payload
      state.selectorsInitialized =
        state.wineLotBottleQuantity !== undefined &&
        state.deliveryCostCode !== undefined &&
        state.purchaseConditionCode !== undefined
      ls.setObject(state.selectorsLsKey, {
        wineLotBottleQuantity: state.wineLotBottleQuantity ?? null,
        deliveryCostCode: state.deliveryCostCode ?? null,
        vatBehaviorCode: payload,
        purchaseConditionCode: state.purchaseConditionCode ?? null,
      })
    },
    setPurchaseConditionCode: (
      state,
      { payload }: PayloadAction<Array<string | null> | null>
    ) => {
      state.purchaseConditionCode = payload
      state.selectorsInitialized =
        state.wineLotBottleQuantity !== undefined &&
        state.deliveryCostCode !== undefined &&
        state.vatBehaviorCode !== undefined
      ls.setObject(state.selectorsLsKey, {
        wineLotBottleQuantity: state.wineLotBottleQuantity ?? null,
        deliveryCostCode: state.deliveryCostCode ?? null,
        vatBehaviorCode: state.vatBehaviorCode ?? null,
        purchaseConditionCode: payload,
      })
    },
    setSelectorsInitialized: (state, { payload }: PayloadAction<boolean>) => {
      state.selectorsInitialized = payload
    },
    triggerRefetch: state => {
      state.refetchTrigger = state.refetchTrigger + 1
    },

    loadSelectors: (state, { payload }: PayloadAction<string>) => {
      return {
        ...state,
        selectorsLsKey: payload,
        ...readSelectorsFromLs(payload),
      }
    },
    reset: () => {
      return getInitialState()
    },
  },
})

export const { actions, reducer: retailWines } = retailWinesSlice
export const {
  setItems,
  setPageInfo,
  setSortingValue: setSort,
  clear,
  setLoading,
  setQuery,
  setFilters,
  setError,
  reset,
  loadSelectors,
  setSelectedItems,
  setCurrency,
  setWineLotBottleQuantity,
  setDeliveryCostCode,
  setVatBehaviorCode,
  setPurchaseConditionCode,
  setSelectorsInitialized,
  triggerRefetch,
} = actions
